/**
 * Site languages: Armenian (main), English, Russian. The choice is remembered in this browser;
 * a `?lang=hy|en|ru` link overrides it (and is remembered too). Games add their own strings
 * (`addGameStrings`) from their lazy chunks, so the landing page carries none of them.
 */
import i18n, { Resource } from "i18next";
import { initReactI18next } from "react-i18next";
import common from "./locales/common";

export const LANGUAGES = [
  { code: "hy", short: "Հայ", name: "Հայերեն" },
  { code: "en", short: "Eng", name: "English" },
  { code: "ru", short: "Рус", name: "Русский" },
] as const;

export type Lang = (typeof LANGUAGES)[number]["code"];
export const DEFAULT_LANG: Lang = "hy";
const STORAGE_KEY = "brickcity:lang";

const isLang = (v: unknown): v is Lang => LANGUAGES.some((l) => l.code === v);

function stored(): Lang | null {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return isLang(v) ? v : null;
  } catch {
    return null; // storage blocked: fall back to the default every visit
  }
}

function remember(lang: Lang) {
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Storage blocked: the choice lasts for this page only.
  }
}

/** `?lang=` first, then the remembered choice, then Armenian. */
export function initialLanguage(): Lang {
  const param = new URLSearchParams(window.location.search).get("lang");
  if (isLang(param)) {
    remember(param);
    return param;
  }
  return stored() ?? DEFAULT_LANG;
}

/** Switches the whole site and remembers the choice in this browser. */
export function setLanguage(lang: Lang) {
  remember(lang);
  void i18n.changeLanguage(lang);
}

/** The current language, as one of ours. */
export function currentLanguage(): Lang {
  return isLang(i18n.resolvedLanguage) ? i18n.resolvedLanguage : DEFAULT_LANG;
}

/** Registers a game's strings under its own namespace (called from the game's chunk). */
export function addGameStrings(ns: string, strings: Record<Lang, Record<string, unknown>>) {
  LANGUAGES.forEach(({ code }) => i18n.addResourceBundle(code, ns, strings[code], true, true));
}

const resources: Resource = {
  hy: { common: common.hy },
  en: { common: common.en },
  ru: { common: common.ru },
};

i18n.on("languageChanged", (lng) => {
  document.documentElement.lang = lng;
});

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage(),
  fallbackLng: "en",
  supportedLngs: LANGUAGES.map((l) => l.code),
  defaultNS: "common",
  ns: ["common"],
  interpolation: { escapeValue: false }, // React escapes already
  returnNull: false,
  react: { useSuspense: false },
});

document.documentElement.lang = i18n.language;

export default i18n;
